import { formatMoney } from '../formatting/money'
import type { ChartTheme } from './chartTheme'
import type { ChartMilestone, WealthChartSource } from './types'

/** Where a figure is written, relative to its own dot. */
export type DotSide = 'above' | 'below' | 'beside'

/** One figure called out on a line, at the age it belongs to. */
export type WealthDot = {
  age: string
  value: number
  /**
   * The same figure on the plan's own run, where both are drawn. Its presence
   * turns the label from an amount into a change.
   */
  baseline?: number
  /** Decided by `retirementDots`, which can see both lines and the axis. */
  side: DotSide
}

/**
 * How near the top of the axis a dot can sit before the milestone's own chip
 * is in the way of a figure written above it.
 *
 * The chips hang from the top of the grid, so a line peaking at retirement —
 * which is where a plan usually peaks — puts its figure straight through one.
 */
const CHIP_BAND = 0.78

/**
 * How near the bottom of the axis a dot can sit before the age labels are in
 * the way of a figure written under it.
 *
 * The liquid line runs along the floor of the plot on an early plan, where
 * every figure under it lands in the row of ages.
 */
const AXIS_BAND = 0.1

/**
 * How far apart two figures must be, as a fraction of the axis, to both be
 * written on the same side of their dots.
 *
 * Only reached where an edge has pushed the two onto one side. Closer than
 * this they overlap, so the liquid one is written beside its dot instead —
 * dropping it would lose a figure the chart is drawing a line for.
 */
const MIN_GAP = 0.08

/** What ECharts calls each of them. */
const POSITIONS: Record<DotSide, string> = {
  above: 'top',
  below: 'bottom',
  beside: 'right',
}

export type RetirementDots = {
  networth?: WealthDot
  liquidAssets?: WealthDot
}

const dotAt = (
  point: WealthChartSource[number],
  value: number,
  side: DotSide,
  against: WealthChartSource | undefined,
  read: (match: WealthChartSource[number]) => number | null
): WealthDot => {
  const match = against?.find(entry => entry.age === point.age)
  const baseline = match ? read(match) : null

  return baseline == null
    ? { age: point.age, value, side }
    : { age: point.age, value, baseline, side }
}

/**
 * What each wealth line is worth at retirement, and where each figure goes.
 *
 * Net worth is never below liquid assets, so writing one above its dot and the
 * other below keeps them apart however close the lines run. Each is turned
 * back where the edge it faces is occupied: the milestone's chip hangs from
 * the top of the grid, and the ages run along the bottom. Where that leaves
 * both on one side and too close to read, the liquid figure moves beside its
 * dot, which is a direction nothing else is using.
 *
 * `axisMax` is what makes any of it possible: a figure's distance from the
 * edges of the plot is the only thing that says what is in its way, and the
 * axis is the only place that distance is known.
 */
export const retirementDots = (
  source: WealthChartSource,
  milestones: ChartMilestone[],
  /** The plan's own run, where both are drawn and the dots report the change. */
  against?: WealthChartSource,
  axisMax?: number
): RetirementDots => {
  const retirement = milestones.find(
    milestone => milestone.kind === 'retirement'
  )
  if (!retirement) return {}

  const point = source.find(entry => entry.age === retirement.age)
  if (!point || point.networth == null) return {}

  const height = (value: number): number | undefined =>
    axisMax !== undefined && axisMax > 0 ? value / axisMax : undefined

  const networthHeight = height(point.networth)
  const networthSide: DotSide =
    networthHeight !== undefined && networthHeight > CHIP_BAND
      ? 'below'
      : 'above'

  const networth = dotAt(
    point,
    point.networth,
    networthSide,
    against,
    match => match.networth
  )
  const liquid = point.liquidAssets
  if (liquid == null || liquid === point.networth) return { networth }

  const liquidHeight = height(liquid)
  const liquidSide: DotSide =
    liquidHeight !== undefined && liquidHeight < AXIS_BAND ? 'above' : 'below'

  // On one side, and too close to be read apart: sideways is the one direction
  // neither the chip nor the ages have taken.
  const gap = height(point.networth - liquid)
  const crowded =
    networthSide === liquidSide && gap !== undefined && gap < MIN_GAP

  return {
    networth,
    liquidAssets: dotAt(
      point,
      liquid,
      crowded ? 'beside' : liquidSide,
      against,
      match => match.liquidAssets
    ),
  }
}

/**
 * A signed change, as this app writes one.
 *
 * `formatMoney` puts a negative in parentheses, which is right for a balance and
 * wrong for a change — so the magnitude goes in and the sign is written here.
 */
const formatChange = (change: number): string =>
  `${change < 0 ? '-' : '+'}${formatMoney(Math.abs(change), 'coarse')}`

/**
 * A dot on a line, with what the line is worth there written above it.
 *
 * Coarse money, as the axis is: a figure to the dollar on a chart scaled in
 * hundreds of thousands reads as more certain than a projection can be.
 *
 * Where both runs are drawn the label is the change instead, in the line's own
 * colour so it cannot read as a third series, with the direction in the ground
 * under it — terracotta where the plan ends up behind, grey where it does not.
 */
export const buildMarkPoint = (
  theme: ChartTheme,
  color: string,
  dot: WealthDot | undefined
): object | undefined => {
  if (!dot) return undefined

  const change =
    dot.baseline === undefined ? undefined : dot.value - dot.baseline
  const ground =
    change === undefined
      ? theme.markLine.labelBackgroundColor
      : change < 0
        ? theme.markPoint.lossBackgroundColor
        : theme.markPoint.gainBackgroundColor

  return {
    /*
     * Pressable, and it opens the year it names — the same bargain the
     * milestone's chip makes, and the figure is that milestone's own.
     *
     * No emphasis state to go with it: taking events hands ECharts a hovered
     * and a selected appearance, and on a touch screen the tap that opens the
     * year would leave the dot wearing one.
     */
    silent: false,
    emphasis: { disabled: true },
    symbol: 'circle',
    symbolSize: 7,
    itemStyle: { color },
    label: {
      show: true,
      position: POSITIONS[dot.side],
      distance: 6,
      // A literal, not a template: ECharts renders a string with no braces in
      // it as it stands.
      formatter:
        change === undefined
          ? formatMoney(dot.value, 'coarse')
          : formatChange(change),
      color,
      fontFamily: theme.markPoint.fontFamily,
      fontSize: theme.markPoint.fontSize,
      // Stated, because ECharts lays the text on its baseline inside the
      // background box and leaves it sitting high in one without it.
      lineHeight: theme.markPoint.fontSize + 1,
      backgroundColor: ground,
      padding: [3, 6],
    },
    data: [{ coord: [dot.age, dot.value] }],
  }
}
